import { Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';

import { VECTOR3_Y, rotateVector3 } from '@egr/wcf/modules/core/utils/MathUtils';
import { View } from '@egr/wcf/modules/core/view';

type MoveDirections = 'front' | 'back' | 'left' | 'right' | 'idle';
export type MoveSpeed = 'idle' | 'low' | 'mid' | 'high';
export type RotationDirections = 'top' | 'down' | 'left' | 'right' | 'idle';

export enum ViewHeightType {
    CHILD = 0.9,
    PARENT = 1.8,
    BIRD = 10.0
}

export class Person {
    public static setHeight(view: View, height: ViewHeightType | number): void {
        const pos: Vector3 = view.cameraControl.position.clone();
        pos.y = height;
        view.cameraControl.setPosition(pos);
        view.viewer.requestRenderFrame();
    }

    public static toggleHeight(view: View): void {
        const pos: Vector3 = view.cameraControl.position;
        if (pos.y < ViewHeightType.CHILD || pos.y >= ViewHeightType.BIRD) {
            pos.y = ViewHeightType.CHILD;
        } else if (pos.y >= ViewHeightType.CHILD && pos.y < ViewHeightType.PARENT) {
            pos.y = ViewHeightType.PARENT;
        } else if (pos.y >= ViewHeightType.PARENT && pos.y < ViewHeightType.BIRD) {
            pos.y = ViewHeightType.BIRD;
        }
        view.cameraControl.setPosition(pos);
    }

    public static doNothing(view: View): void {
        view.cameraControl.setDirection(view.cameraControl.direction);
    }

    public static lookAhead(view: View): void {
        const direction: Vector3 = view.cameraControl.direction.clone();
        const lookAhead: Vector3 = new Vector3(direction.x, 0, direction.z);
        view.cameraControl.setDirection(lookAhead);
    }

    public static moveSimple(view: View, direction: MoveDirections, speed: number = 0.1): void {
        if (direction === 'idle') {
            return;
        }
        const pos: Vector3 = view.cameraControl.position;
        const storedY: number = pos.y;
        let dir: Vector3 = view.cameraControl.direction;
        if (direction === 'left' || direction === 'right') {
            dir = new Vector3(dir.z, dir.y, -dir.x);
        }
        const directionSpeed: Vector3 = dir.multiply(new Vector3(speed, speed, speed));
        switch (direction) {
            case 'front':
                pos.addInPlace(directionSpeed);
                break;
            case 'back':
                pos.addInPlace(directionSpeed.negate());
                break;
            case 'left':
                pos.addInPlace(directionSpeed.negate());
                break;
            case 'right':
                pos.addInPlace(directionSpeed);
                break;
            default:
                break;
        }
        pos.y = storedY;
        view.cameraControl.setPosition(pos);
    }

    public static move(view: View, angle: number, speed: MoveSpeed): void {
        if (speed === 'idle') {
            return;
        }
        const pos: Vector3 = view.cameraControl.position;
        const storedY: number = pos.y;
        const dir: Vector3 = view.cameraControl.direction;
        const quarternion: Quaternion = Quaternion.RotationAxis(VECTOR3_Y, angle * Math.PI / 180);
        let dirSpeed: number = 0;
        switch (speed) {
            case 'low':
                dirSpeed = 0.02;
                break;
            case 'mid':
                dirSpeed = 0.05;
                break;
            case 'high':
                dirSpeed = 0.1;
                break;
            default:
                break;
        }
        const newDir: Vector3 = rotateVector3(dir, quarternion).multiplyInPlace(new Vector3(dirSpeed, dirSpeed, dirSpeed));
        pos.addInPlace(newDir);
        pos.y = storedY;
        view.cameraControl.setPosition(pos);
    }

    public static rotate(view: View, direction: RotationDirections, speed: number = 0.01): void {
        if (direction === 'idle') {
            return;
        }
        const dir: Vector3 = view.cameraControl.direction;
        const right: Vector3 = view.cameraControl.right;
        // if (dir.y < -0.5 && direction === 'top') {
        if (dir.y > 0.5 && direction === 'top') {
            return;
        }
        // if (dir.y > 0.5 && direction === 'down') {
        if (dir.y < -0.5 && direction === 'down') {
            return;
        }
        let quarternion: Quaternion;
        switch (direction) {
            case 'left':
                quarternion = Quaternion.RotationAxis(VECTOR3_Y, -speed);
                break;
            case 'right':
                quarternion = Quaternion.RotationAxis(VECTOR3_Y, speed);
                break;
            case 'top':
                quarternion = Quaternion.RotationAxis(right, -speed);
                break;
            case 'down':
                quarternion = Quaternion.RotationAxis(right, speed);
                break;
            default:
                return;
        }
        const newDir: Vector3 = rotateVector3(dir, quarternion);
        view.cameraControl.setDirection(newDir);
    }
}
